/**
 * The customer (or supplier) list on paper — exactly the rows on screen, with
 * the filters, search and sort the operator set.
 *
 * Printed IN PLACE rather than on a page of its own: the list holds the whole
 * book (thousands of customers, paged in from the server), and a separate page
 * would load all of it again and drop the filters on the way back. So the paper
 * renders into a portal beside the app, hidden on screen, and the print
 * stylesheet hides everything else — the app shell is a fixed-height scroller
 * that would otherwise print as one cut-off page. Styled like the statement of
 * account (CustomerStatementPrintPage), so the shop's papers match.
 */
import type { Party } from '@erp/shared';
import Decimal from 'decimal.js';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { api } from '../../lib/api.js';
import { formatDateTime } from '../../lib/intl.js';
import { formatMoney } from '../../lib/money.js';
import { assetUrl } from '../../lib/publicApi.js';
import { addressText, type Letterhead } from './CustomerStatementPrintPage.js';

export interface PartiesListPrintProps {
  rows: Party[];
  side?: 'customer' | 'vendor';
  /** The filters in force, already worded ("Account type: جملة", "With debts"…). */
  filters: string[];
  showBranch: boolean;
  /** Called once the print dialog closes. */
  onDone: () => void;
}

export function PartiesListPrint({ rows, side, filters, showBranch, onDone }: PartiesListPrintProps): JSX.Element {
  const { t, i18n } = useTranslation('parties');
  const lng = i18n.language;
  const [head, setHead] = useState<Letterhead | null | undefined>(undefined);
  const [logoSettled, setLogoSettled] = useState(false);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    let on = true;
    // Best effort, like the statement: the list prints without a letterhead.
    void api().get<Letterhead>('/tenants/current/paper')
      .then((h) => { if (on) setHead(h.data); })
      .catch(() => { if (on) setHead(null); });
    return () => { on = false; };
  }, []);

  const seller = head?.seller ?? null;
  const shopName = seller?.storeName?.trim() || head?.name || '';
  const logo = seller?.logoAttachmentId && seller.slug
    ? assetUrl(`/api/v1/storefront/${seller.slug}/media/${seller.logoAttachmentId}`)
    : null;
  const website = (seller?.socialLinks?.website ?? '').trim().replace(/^https?:\/\//, '').replace(/\/$/, '');
  const shopAddress = addressText(seller?.address);

  // A logo that never answers must not hold the print back for ever.
  useEffect(() => {
    if (head === undefined || !logo) return;
    const timer = window.setTimeout(() => setLogoSettled(true), 3000);
    return () => window.clearTimeout(timer);
  }, [head, logo]);

  // Print once the letterhead has answered and its logo has loaded (or failed):
  // any earlier and the paper comes out with an empty box where the logo goes.
  const ready = head !== undefined && (!logo || logoSettled);
  useEffect(() => {
    if (!ready) return;
    const done = (): void => onDoneRef.current();
    window.addEventListener('afterprint', done, { once: true });
    const raf = window.requestAnimationFrame(() => window.print());
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('afterprint', done);
    };
  }, [ready]);

  const money = (a: string, ccy: string): string => formatMoney(a, { locale: lng, currency: ccy });
  const isCustomer = side === 'customer';
  const isVendor = side === 'vendor';
  const withMoney = isCustomer || isVendor;

  // What the printed rows owe (or are owed), per currency.
  const totals = new Map<string, Decimal>();
  let withDebt = 0;
  for (const p of rows) {
    if (p.debts && p.debts.length > 0) withDebt += 1;
    for (const d of p.debts ?? []) totals.set(d.currencyCode, (totals.get(d.currencyCode) ?? new Decimal(0)).plus(d.amount));
  }
  const totalList = [...totals.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  const debtLabel = isVendor ? t('list.columns.owed', { defaultValue: 'Owed' }) : t('list.columns.debts', { defaultValue: 'Debts' });

  const title = isCustomer
    ? t('listPrint.titleCustomers', { defaultValue: 'Customer list' })
    : isVendor
      ? t('listPrint.titleSuppliers', { defaultValue: 'Supplier list' })
      : t('listPrint.titleAll', { defaultValue: 'Customers & suppliers' });

  const accountType = (p: Party): string =>
    p.priceListId && p.priceListName
      ? p.priceListName
      : p.requestedPriceListId
        ? `${p.priceListName ?? ''} · ${t('list.typePending', { defaultValue: 'pending' })}`
        : t('list.typeNormal', { defaultValue: 'Normal' });

  const cols = 4 + (isCustomer ? 1 + (showBranch ? 1 : 0) : 1) + (withMoney ? 1 : 0);

  return createPortal(
    <div className="pl-root" dir={i18n.dir(lng)} lang={lng}>
      <style>{CSS}</style>
      <header className="pl-head">
        <div className="pl-seller">
          {logo ? (
            <img
              className="pl-logo"
              src={logo}
              alt={shopName}
              onLoad={() => setLogoSettled(true)}
              onError={() => setLogoSettled(true)}
            />
          ) : (
            shopName && <div className="pl-mk" aria-hidden>{shopName.slice(0, 2)}</div>
          )}
          <div>
            <div className="pl-shop">{shopName}</div>
            {seller?.tagline?.trim() && <div className="pl-tag">{seller.tagline.trim()}</div>}
            <div className="pl-contact">
              {seller?.contactPhone && <span dir="ltr">{seller.contactPhone}</span>}
              {seller?.contactEmail && <span dir="ltr">{seller.contactEmail}</span>}
              {website && <span dir="ltr">{website}</span>}
            </div>
            {shopAddress && <div className="pl-contact"><bdi>{shopAddress}</bdi></div>}
          </div>
        </div>
        <h1 className="pl-title">{title}</h1>
      </header>

      <div className="pl-meta">
        <div>
          <span className="k">{t('listPrint.printedOn', { defaultValue: 'Printed on' })}</span>
          <span className="v">{formatDateTime(new Date(), { locale: lng }, { dateStyle: 'medium', timeStyle: 'short' })}</span>
        </div>
        <div>
          <span className="k">{t('listPrint.rows', { defaultValue: 'Names' })}</span>
          <span className="v">{rows.length.toLocaleString(lng)}</span>
        </div>
        {withMoney && (
          <div>
            <span className="k">{isVendor ? t('listPrint.withOwed', { defaultValue: 'We owe' }) : t('listPrint.withDebts', { defaultValue: 'Owe us' })}</span>
            <span className="v">{withDebt.toLocaleString(lng)}</span>
          </div>
        )}
        <div className="wide">
          <span className="k">{t('listPrint.filter', { defaultValue: 'Showing' })}</span>
          <span className="v">{filters.length ? filters.join(' · ') : t('listPrint.noFilter', { defaultValue: 'Everyone' })}</span>
        </div>
      </div>

      {withMoney && totalList.length > 0 && (
        <div className="pl-sum">
          {totalList.map(([ccy, amount]) => (
            <div key={ccy}>
              <span className="k">{debtLabel} · {ccy}</span>
              <span className="v" dir="ltr">{money(amount.toFixed(4), ccy)}</span>
            </div>
          ))}
        </div>
      )}

      <table className="pl-lines">
        <thead>
          <tr>
            <th className="n">#</th>
            <th>{t('list.columns.code')}</th>
            <th>{t('list.columns.name')}</th>
            {!side && <th>{t('list.columns.type')}</th>}
            <th>{t('list.columns.phone', { defaultValue: 'Phone' })}</th>
            {isCustomer && <th>{t('list.columns.accountType', { defaultValue: 'Account type' })}</th>}
            {isCustomer && showBranch && <th>{t('branch.label')}</th>}
            {!isCustomer && <th>{t('list.columns.email')}</th>}
            {withMoney && <th className="m">{debtLabel}</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((p, i) => (
            <tr key={p.id}>
              <td className="n">{i + 1}</td>
              <td className="code"><span dir="ltr">{p.code}</span></td>
              <td className="nm">
                <bdi>{p.displayName}</bdi>
                {!p.isActive && <span className="off">{t('status.inactive')}</span>}
              </td>
              {!side && <td>{t(`type.${p.partyType}`)}</td>}
              <td className="ph"><span dir="ltr">{p.phone ?? ''}</span></td>
              {isCustomer && <td>{accountType(p)}</td>}
              {isCustomer && showBranch && <td>{p.warehouseId ? (p.warehouseName ?? '') : t('branch.sharedShort')}</td>}
              {!isCustomer && <td className="em"><span dir="ltr">{p.email ?? ''}</span></td>}
              {withMoney && (
                <td className="m b">
                  <span dir="ltr">{(p.debts ?? []).map((d) => money(d.amount, d.currencyCode)).join(' · ')}</span>
                </td>
              )}
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={cols} className="none">{t('list.empty')}</td></tr>
          )}
        </tbody>
        {withMoney && totalList.length > 0 && (
          <tfoot>
            <tr>
              <td colSpan={cols - 1}>{t('listPrint.total', { defaultValue: 'Total' })}</td>
              <td className="m"><span dir="ltr">{totalList.map(([ccy, a]) => money(a.toFixed(4), ccy)).join(' · ')}</span></td>
            </tr>
          </tfoot>
        )}
      </table>

      <footer className="pl-foot">
        <span className="nm">{shopName}</span>
        {seller?.contactPhone && <span dir="ltr">{seller.contactPhone}</span>}
        {website && <span dir="ltr">{website}</span>}
      </footer>
    </div>,
    document.body,
  );
}

const CSS = `
.pl-root { display: none; }
@media print {
  @page {
    size: A4; margin: 11mm 10mm 13mm;
    @bottom-center { content: counter(page) " / " counter(pages); font-size: 8.5px; color: #8a92a6; }
  }
  html, body { height: auto !important; overflow: visible !important; background: #fff !important; }
  body > *:not(.pl-root) { display: none !important; }
  .pl-root { display: block; color: #3a4252; background: #fff; font-size: 10.5px;
    font-family: var(--font-sans-arabic), var(--font-sans), system-ui, sans-serif; }
}
.pl-root * { box-sizing: border-box; }
.pl-head { display: flex; align-items: center; justify-content: space-between; gap: 18px;
  padding-bottom: 12px; border-bottom: 3px solid #16233b; }
.pl-seller { display: flex; align-items: center; gap: 14px; min-width: 0; }
.pl-logo { width: 70px; max-height: 70px; object-fit: contain; }
.pl-mk { width: 56px; height: 56px; border-radius: 12px; background: #16233b; color: #fff; display: grid; place-items: center;
  font-size: 19px; font-weight: 800; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.pl-shop { font-size: 17px; font-weight: 800; color: #16233b; line-height: 1.25; }
.pl-tag { font-size: 10px; color: #6b7489; }
.pl-contact { font-size: 9.5px; color: #545b72; display: flex; flex-wrap: wrap; gap: 2px 12px; margin-top: 2px; }
.pl-title { margin: 0; font-size: 21px; font-weight: 800; color: #16233b; white-space: nowrap; }

.pl-meta { display: flex; margin-top: 12px; border: 1px solid #e3e6ee; border-radius: 8px; overflow: hidden; }
.pl-meta > div { flex: 1 1 0; display: flex; flex-direction: column; gap: 1px; padding: 6px 11px; min-width: 0; }
.pl-meta > div.wide { flex-grow: 2.4; }
.pl-meta > div + div { border-inline-start: 1px solid #e3e6ee; }
.pl-meta .k, .pl-sum .k { font-size: 8.5px; font-weight: 700; letter-spacing: .05em; text-transform: uppercase; color: #8a92a6; }
.pl-meta .v { font-size: 11px; font-weight: 800; color: #16233b; }

.pl-sum { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 10px; }
.pl-sum > div { flex: 1 1 0; min-width: 150px; display: flex; flex-direction: column; gap: 2px; padding: 7px 11px; border-radius: 8px;
  background: #16233b; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.pl-sum .k { color: #c9d1e3; }
.pl-sum .v { font-size: 13px; font-weight: 800; color: #fff; font-variant-numeric: tabular-nums; text-align: start; }

.pl-lines { width: 100%; margin-top: 12px; border-collapse: collapse; font-size: 10px; }
.pl-lines thead { display: table-header-group; }
.pl-lines tfoot { display: table-row-group; }
.pl-lines th { background: #16233b; color: #fff; font-weight: 700; padding: 6px; text-align: start; white-space: nowrap;
  -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.pl-lines td { padding: 4.5px 6px; border-top: 1px solid #edf0f6; vertical-align: top; text-align: start; }
.pl-lines tbody tr:nth-child(even) td { background: #f8fafd; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.pl-lines tr { break-inside: avoid; }
.pl-lines .n { width: 30px; color: #8a92a6; text-align: center; font-variant-numeric: tabular-nums; }
.pl-lines .code { font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; font-size: 9.5px; color: #545b72; white-space: nowrap; }
.pl-lines .nm { font-weight: 700; color: #16233b; }
.pl-lines .off { margin-inline-start: 6px; padding: 0 5px; border: 1px solid #c4cad6; border-radius: 4px; font-size: 8.5px; font-weight: 600; color: #8a92a6; }
.pl-lines .ph, .pl-lines .em { white-space: nowrap; font-variant-numeric: tabular-nums; }
.pl-lines .m { text-align: end; white-space: nowrap; font-variant-numeric: tabular-nums; }
.pl-lines .b { font-weight: 800; color: #b42318; }
.pl-lines .none { text-align: center; color: #8a92a6; padding: 14px; }
.pl-lines tfoot td { border-top: 2px solid #16233b; font-weight: 800; color: #16233b; padding: 7px 6px; }

.pl-foot { margin-top: 16px; padding-top: 8px; border-top: 1px solid #e3e6ee; display: flex; flex-wrap: wrap; justify-content: center;
  gap: 2px 14px; font-size: 9px; color: #6b7489; break-inside: avoid; }
.pl-foot .nm { font-weight: 700; color: #3a4252; }
`;
